import React, { useState } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../../../config";
import {
  getSettlementBatchPrintCvHtml,
  getSettlementBatchPrintData,
} from "../../../services/settlementApi";

let pdfLibLoaderPromise = null;

const isAbsoluteHttpUrl = (value) => /^https?:\/\//i.test(String(value ?? "").trim());

const resolveS3PdfFileUrl = (fileName) => {
  const raw = String(fileName ?? "").trim();
  return isAbsoluteHttpUrl(raw) ? raw : "";
};

const buildBatchPisAndForm2307Urls = (rows) => {
  const urls = [];

  rows.forEach((row) => {
    const pisNoPpdUrl = resolveS3PdfFileUrl(row?.pis_no_ppd_file);
    const form2307Url = resolveS3PdfFileUrl(row?.form_2307_file);

    if (pisNoPpdUrl) urls.push(pisNoPpdUrl);
    if (form2307Url) urls.push(form2307Url);
  });

  return Array.from(new Set(urls));
};

const getPdfLibScriptCandidates = () => {
  const scriptSources = ["/vendor/pdf-lib.min.js"];

  try {
    const apiOrigin = new URL(BASE_URL).origin;
    scriptSources.push(`${apiOrigin}/vendor/pdf-lib.min.js`);
  } catch {
    // Ignore invalid BASE_URL parsing; local source will still be attempted.
  }

  return Array.from(new Set(scriptSources));
};

const waitForScriptLoad = (script) =>
  new Promise((resolve, reject) => {
    const handleLoad = () => {
      script.dataset.loaded = "true";
      resolve();
    };
    const handleError = () => reject(new Error(`Failed to load PDF library from ${script.src}`));

    script.addEventListener("load", handleLoad, { once: true });
    script.addEventListener("error", handleError, { once: true });
  });

const loadPdfLibFromScript = async (source) => {
  const normalizedSource = new URL(source, window.location.origin).toString();
  const existingScript = Array.from(document.querySelectorAll("script[data-pdf-lib='true']")).find((script) => {
    try {
      return new URL(script.src, window.location.origin).toString() === normalizedSource;
    } catch {
      return false;
    }
  });

  if (existingScript) {
    if (window.PDFLib?.PDFDocument) return window.PDFLib;
    const scriptAlreadyFinished =
      existingScript.dataset.loaded === "true" ||
      existingScript.readyState === "complete" ||
      existingScript.readyState === "loaded";
    if (scriptAlreadyFinished) {
      throw new Error("PDF library loaded but PDFDocument is unavailable.");
    }
    await waitForScriptLoad(existingScript);
    if (window.PDFLib?.PDFDocument) return window.PDFLib;
    throw new Error("PDF library loaded but PDFDocument is unavailable.");
  }

  const script = document.createElement("script");
  script.src = normalizedSource;
  script.async = true;
  script.crossOrigin = "anonymous";
  script.dataset.pdfLib = "true";
  document.body.appendChild(script);

  await waitForScriptLoad(script);
  if (window.PDFLib?.PDFDocument) return window.PDFLib;

  throw new Error("PDF library loaded but PDFDocument is unavailable.");
};

const ensurePdfLibLoaded = async () => {
  if (typeof window === "undefined") {
    throw new Error("Batch print is only available in browser mode.");
  }

  if (window.PDFLib?.PDFDocument) {
    return window.PDFLib;
  }

  if (!pdfLibLoaderPromise) {
    pdfLibLoaderPromise = (async () => {
      let lastError = null;

      for (const source of getPdfLibScriptCandidates()) {
        try {
          return await loadPdfLibFromScript(source);
        } catch (error) {
          lastError = error;
        }
      }

      throw lastError ?? new Error("Failed to load PDF library.");
    })().catch((error) => {
      pdfLibLoaderPromise = null;
      throw error;
    });
  }

  return pdfLibLoaderPromise;
};

const mergePdfUrls = async (pdfUrls, onProgress) => {
  const { PDFDocument } = await ensurePdfLibLoaded();
  const merged = await PDFDocument.create();

  let success = 0;
  let failed = 0;

  for (const url of pdfUrls) {
    try {
      const response = await fetch(url, {
        method: "GET",
        mode: "cors",
        credentials: "omit",
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const bytes = await response.arrayBuffer();
      const source = await PDFDocument.load(bytes, { ignoreEncryption: true });
      const copiedPages = await merged.copyPages(source, source.getPageIndices());
      copiedPages.forEach((page) => merged.addPage(page));
      success += 1;
    } catch {
      failed += 1;
    }

    onProgress?.({ processed: success + failed, total: pdfUrls.length, success, failed });
  }

  if (merged.getPageCount() === 0) {
    throw new Error("No valid PDF files were found in the selected CV range.");
  }

  const mergedBytes = await merged.save();
  return { mergedBytes, success, failed };
};

const BatchPrintPisAndForm2307Panel = ({
  onClose,
  autoCloseOnSuccess = false,
}) => {
  const [batchPrintRange, setBatchPrintRange] = useState({ start_cv: "", end_cv: "" });
  const [batchPrintProgress, setBatchPrintProgress] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const validateBatchPrintRange = () => {
    const startCv = String(batchPrintRange.start_cv ?? "").trim();
    const endCv = String(batchPrintRange.end_cv ?? "").trim();

    if (!startCv || !endCv) {
      toast.warn("Please enter CV range.");
      return false;
    }

    if (!/^\d+$/.test(startCv) || !/^\d+$/.test(endCv)) {
      toast.warn("CV range must be numeric.");
      return false;
    }

    if (Number(startCv) > Number(endCv)) {
      toast.warn("CV Start # cannot be greater than CV End #.");
      return false;
    }

    return true;
  };

  const handleBatchPrintPisAndForm2307 = async () => {
    if (!validateBatchPrintRange()) return;

    setSubmitting(true);
    setBatchPrintProgress(null);

    try {
      const res = await getSettlementBatchPrintData(batchPrintRange.start_cv, batchPrintRange.end_cv);
      const data = res.data.data ?? [];

      if (data.length === 0) {
        toast.info("No records found for the given CV range.");
        return;
      }

      const pdfUrls = buildBatchPisAndForm2307Urls(data);
      if (pdfUrls.length === 0) {
        toast.info("No PIS/Form 2307 files found for the selected CV range.");
        return;
      }

      setBatchPrintProgress({
        processed: 0,
        total: pdfUrls.length,
        success: 0,
        failed: 0,
      });

      const { mergedBytes, success, failed } = await mergePdfUrls(pdfUrls, setBatchPrintProgress);
      const blob = new Blob([mergedBytes], { type: "application/pdf" });
      const mergedObjectUrl = URL.createObjectURL(blob);
      const printWindow = window.open(mergedObjectUrl, "_blank");

      if (!printWindow) {
        URL.revokeObjectURL(mergedObjectUrl);
        throw new Error("Unable to open print preview. Please allow pop-ups for this site.");
      }

      setTimeout(() => {
        try {
          printWindow.focus();
          printWindow.print();
        } catch {
          // Keep merged PDF tab open even if print invocation fails.
        }
      }, 400);

      setTimeout(() => URL.revokeObjectURL(mergedObjectUrl), 60_000);

      toast.success(`Merged ${success} PDF file${success > 1 ? "s" : ""} for batch print.`);
      if (failed > 0) {
        toast.warn(`${failed} file${failed > 1 ? "s were" : " was"} skipped (missing/corrupted).`);
      }

      if (autoCloseOnSuccess) {
        onClose?.();
      }
    } catch (err) {
      const msg =
        err?.message ||
        err?.response?.data?.message ||
        "Failed to perform batch print.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
      setBatchPrintProgress(null);
    }
  };

  const handleBatchPrintCv = async () => {
    if (!validateBatchPrintRange()) return;

    setSubmitting(true);
    try {
      const res = await getSettlementBatchPrintData(batchPrintRange.start_cv, batchPrintRange.end_cv);
      const data = res.data.data ?? [];
      if (data.length === 0) {
        toast.info("No records found for the given CV range.");
      } else {
        const cvNos = Array.from(
          new Set(
            data
              .map((row) => Number(row?.cv_no))
              .filter((value) => Number.isInteger(value) && value > 0)
          )
        );

        if (cvNos.length === 0) {
          toast.info("No CV numbers found for the selected range.");
          return;
        }

        const cvHtmlRes = await getSettlementBatchPrintCvHtml(cvNos.join(","));
        const html = String(cvHtmlRes?.data?.html ?? "").trim();

        if (!html) {
          throw new Error("Batch Print CV HTML is empty.");
        }

        const printWindow = window.open("", "_blank");
        if (!printWindow) {
          throw new Error("Unable to open print preview. Please allow pop-ups for this site.");
        }

        printWindow.document.open();
        printWindow.document.write(html);
        printWindow.document.close();

        await new Promise((resolve) => {
          let done = false;
          const finish = () => {
            if (done) return;
            done = true;
            resolve();
          };

          const timeoutId = setTimeout(finish, 4000);
          const images = Array.from(printWindow.document.images || []);

          if (images.length === 0) {
            clearTimeout(timeoutId);
            finish();
            return;
          }

          let loaded = 0;
          const checkLoaded = () => {
            loaded += 1;
            if (loaded >= images.length) {
              clearTimeout(timeoutId);
              finish();
            }
          };

          images.forEach((image) => {
            if (image.complete) {
              checkLoaded();
            } else {
              image.addEventListener("load", checkLoaded, { once: true });
              image.addEventListener("error", checkLoaded, { once: true });
            }
          });
        });

        try {
          printWindow.focus();
          printWindow.print();
        } catch {
          // Keep preview window open even if browser blocks direct print trigger.
        }

        toast.success(`Prepared ${cvNos.length} CV voucher(s) for printing.`);
        if (autoCloseOnSuccess) {
          onClose?.();
        }
      }
    } catch (err) {
      const message =
        (typeof err?.response?.data?.message === "string" && err.response.data.message) ||
        err?.message ||
        "Failed to get batch print data.";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setBatchPrintRange({ start_cv: "", end_cv: "" });
    setBatchPrintProgress(null);
  };

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">CV Start #</label>
          <input
            type="number"
            value={batchPrintRange.start_cv}
            onChange={(e) => setBatchPrintRange((prev) => ({ ...prev, start_cv: e.target.value }))}
            className="w-full border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">CV End #</label>
          <input
            type="number"
            value={batchPrintRange.end_cv}
            onChange={(e) => setBatchPrintRange((prev) => ({ ...prev, end_cv: e.target.value }))}
            className="w-full border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
        </div>
      </div>

      {batchPrintProgress?.total > 0 && (
        <p className="mt-3 text-xs text-gray-600">
          Fetching files: {batchPrintProgress.processed}/{batchPrintProgress.total}
          {batchPrintProgress.failed > 0 ? ` (failed: ${batchPrintProgress.failed})` : ""}
        </p>
      )}

      <div className="flex flex-wrap justify-end gap-2 mt-4 pt-3 border-t">
        {onClose ? (
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-1.5 text-sm border rounded hover:bg-gray-100 disabled:opacity-50"
          >
            Close
          </button>
        ) : (
          <button
            onClick={handleReset}
            disabled={submitting}
            className="px-4 py-1.5 text-sm border rounded hover:bg-gray-100 disabled:opacity-50"
          >
            Reset
          </button>
        )}

        <button
          onClick={handleBatchPrintCv}
          disabled={submitting}
          className="px-4 py-1.5 text-sm bg-amber-500 text-white rounded hover:bg-amber-600 disabled:opacity-50"
        >
          {submitting ? "Processing..." : "Batch Print CV"}
        </button>

        <button
          onClick={handleBatchPrintPisAndForm2307}
          disabled={submitting}
          className="px-4 py-1.5 text-sm bg-sky-600 text-white rounded hover:bg-sky-700 disabled:opacity-50"
        >
          {submitting ? "Processing..." : "Batch Print"}
        </button>
      </div>
    </div>
  );
};

export default BatchPrintPisAndForm2307Panel;
